import { m } from "framer-motion";
import { BarChart3, Bell, Home, Search, Package, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

const FeaturesSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      slug: "inventory",
      icon: Package,
      title: t("features.inventory.title"),
      description: t("features.inventory.desc"),
    },
    {
      slug: "expiry-tracking",
      icon: Clock,
      title: t("features.expiry.title"),
      description: t("features.expiry.desc"),
    },
    {
      slug: "push-notifications",
      icon: Bell,
      title: t("features.push.title"),
      description: t("features.push.desc"),
    },
    {
      slug: "filter-search",
      icon: Search,
      title: t("features.filterSearch.title"),
      description: t("features.filterSearch.desc"),
    },
    {
      slug: "household",
      icon: Home,
      title: t("features.household.title"),
      description: t("features.household.desc"),
    },
    {
      slug: "statistics",
      icon: BarChart3,
      title: t("features.stats.title"),
      description: t("features.stats.desc"),
    },
  ];

  return (
    <section id="features" className="py-16 lg:py-32 bg-card">
      <div className="container">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Link
            to="/features"
            className="inline-block px-4 py-1.5 rounded-full bg-fresh-green-light text-fresh-green text-sm font-medium mb-4 hover:bg-fresh-green hover:text-background transition-colors"
          >
            {t("features.badge")}
          </Link>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("features.title")}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("features.subtitle")}
          </p>
        </m.div>

        <m.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {features.map((feature, index) => (
            <Link key={feature.slug} to={`/features/${feature.slug}`}>
              <m.div
                variants={itemVariants}
                className="group p-6 lg:p-8 rounded-2xl bg-background border border-border hover:border-primary/30 hover:shadow-card transition-all duration-300 h-full"
              >
                <div className="w-14 h-14 rounded-xl bg-fresh-green-light flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-7 h-7 text-fresh-green" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </m.div>
            </Link>
          ))}
        </m.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
